package com.banyan.flightviewer.web.state

import kotlinx.browser.window
import kotlinx.coroutines.CompletableDeferred
import kotlinx.coroutines.launch
import kotlinx.coroutines.withTimeoutOrNull

/**
 * On every page load, try to resolve the user's real location and push it
 * into settings so the map viewport + monitoring query track where they
 * actually are.
 *
 * Two-step attempt:
 *  1. **Browser geolocation API** (`navigator.geolocation`). The first
 *     visit shows the native permission prompt. Subsequent visits return
 *     a cached or fresh fix instantly if previously approved, or error
 *     instantly if previously denied — neither re-prompts, so it's safe
 *     to call unconditionally every load.
 *  2. **Server IP geolocation** fallback when the browser API didn't
 *     give us anything AND the current settings are still the factory
 *     default. We don't run the IP fallback on subsequent loads because
 *     that would potentially overwrite a real location the user
 *     approved on an earlier visit.
 *
 * Runs in the background — does NOT block startup. Monitoring picks up
 * the new lat/lon the next cycle (plus we request an immediate refresh
 * on success).
 *
 * Called once from [com.banyan.flightviewer.web.Main] after settings
 * load, before monitoring starts.
 */
fun wireLocationAutoDetect(app: WebAppContainer) {
    app.appState.scope.launch {
        app.appState.setStatusMessage("Detecting location\u2026")

        // Step 1: always attempt browser geolocation. First load → prompt.
        // Subsequent loads → instant resolve (allowed) or instant error
        // (denied), neither re-prompts.
        val fromBrowser = tryBrowserGeolocation()
        if (fromBrowser != null) {
            applyLocation(app, fromBrowser.first, fromBrowser.second, name = null)
            return@launch
        }

        // Step 2: IP fallback. Only when settings are still at factory
        // defaults — i.e. nothing has successfully located this user yet.
        // If there's already a real location saved from a prior visit,
        // leave it alone so we don't downgrade to a less accurate IP fix.
        val current = app.appState.settings.value
        if (current.lat != DEFAULT_LAT || current.lon != DEFAULT_LON) {
            app.appState.setStatusMessage("")
            return@launch
        }
        val detected = app.serverClient.getGeolocation()
        if (detected == null) {
            app.appState.setStatusMessage("")
            return@launch
        }
        applyLocation(app, detected.lat, detected.lon, name = detected.name)
        app.appState.setDetectedLocation(detected)
    }
}

/**
 * Kick the browser's `navigator.geolocation.getCurrentPosition` and
 * suspend until it resolves. Returns `(lat, lon)` on success, null on
 * denial / unavailable / timeout / any other error. Wrapped in a 20s
 * outer timeout so a user who dismisses the prompt without picking
 * allow-or-block doesn't hang startup forever.
 */
private suspend fun tryBrowserGeolocation(): Pair<Double, Double>? {
    val geolocation = window.navigator.asDynamic().geolocation ?: return null

    val deferred = CompletableDeferred<Pair<Double, Double>?>()
    try {
        geolocation.getCurrentPosition(
            { position: dynamic ->
                val lat = (position.coords.latitude as? Number)?.toDouble()
                val lon = (position.coords.longitude as? Number)?.toDouble()
                if (lat != null && lon != null) deferred.complete(lat to lon)
                else deferred.complete(null)
                Unit
            },
            { _: dynamic ->
                // Denial, timeout, or position-unavailable all land here.
                // We don't differentiate -- fallback path is the same.
                deferred.complete(null)
                Unit
            },
            js("({ enableHighAccuracy: false, timeout: 15000, maximumAge: 600000 })"),
        )
    } catch (_: Throwable) {
        deferred.complete(null)
    }
    return withTimeoutOrNull(20_000) { deferred.await() }
}

/**
 * Persist a detected location to settings, no-op if the coordinates
 * haven't changed (avoids an unnecessary localStorage write + refresh
 * signal on every reload when the user's location is stable).
 */
private fun applyLocation(
    app: WebAppContainer,
    lat: Double,
    lon: Double,
    name: String?,
) {
    val current = app.appState.settings.value
    if (current.lat == lat && current.lon == lon) {
        app.appState.setStatusMessage(
            if (!name.isNullOrBlank()) "Location: $name" else ""
        )
        return
    }
    val updated = current.copy(lat = lat, lon = lon)
    app.settingsManager.saveSettings(updated)
    app.appState.setStatusMessage(
        if (!name.isNullOrBlank()) "Location: $name" else "Location set"
    )
    app.appState.requestImmediateRefresh()
}

private const val DEFAULT_LAT: Double = 40.7128
private const val DEFAULT_LON: Double = -74.0060
